import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';

function ComparisonModal({ comparisonResult, uploadedFile, onClose, uploadDate, user }) {
  const [saving, setSaving] = useState(false);

  const logComparisonValues = () => {
    if (!comparisonResult.matching_subscriptions || comparisonResult.matching_subscriptions.length === 0) {
      console.log('=== SOF TO CHARGEBEE COMPARISON LOG ===');
      console.log('No matching subscriptions found for comparison');
      console.log('========================================');
      return;
    }

    const subscription = comparisonResult.matching_subscriptions[0].subscription || comparisonResult.matching_subscriptions[0];
    const customer = comparisonResult.matching_subscriptions[0].customer || {};

    console.log('=== SOF TO CHARGEBEE COMPARISON LOG ===');
    
    // Customer comparison
    const sofCustomer = comparisonResult.sof_data.billing_contact_information?.customer || '';
    const chargebeeCustomer = customer.company || '';
    console.log(`Customer - SOF: "${sofCustomer}" | Chargebee: "${chargebeeCustomer}"`);
    
    // Email comparison
    const sofEmail = comparisonResult.sof_data.billing_contact_information?.email_address || '';
    const chargebeeEmail = customer.email || '';
    console.log(`Email - SOF: "${sofEmail}" | Chargebee: "${chargebeeEmail}"`);
    
    // Total Amount comparison
    const sofAmount = comparisonResult.sof_data.product_information?.total_net_price || '';
    const chargebeeAmountFormatted = formatCurrency(subscription.mrr || 0, subscription.currency_code || 'USD');
    console.log(`Total Amount - SOF: "${sofAmount}" | Chargebee: "${chargebeeAmountFormatted}"`);
    
    // Product Instance Domain comparison
    const sofProductDomain = comparisonResult.sof_data.product_information?.product_instance_domain || '';
    const chargebeeProductDomain = subscription.cf_values?.product_instance_domain || 
                                 subscription.cf_product_instance_domain || 
                                 subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value || 
                                 '';
    
    // Also check alternative locations for domain
    let alternativeChargebeeDomain = '';
    for (const key in subscription) {
      if (key.includes('product_instance_domain') || key.includes('domain')) {
        if (subscription[key] && !alternativeChargebeeDomain) {
          alternativeChargebeeDomain = subscription[key];
        }
      }
    }
    
    const finalChargebeeDomain = chargebeeProductDomain || alternativeChargebeeDomain || subscription.id || '';
    console.log(`Product Instance Domain - SOF: "${sofProductDomain}" | Chargebee: "${finalChargebeeDomain}"`);
    
    console.log('========================================');
  };

  useEffect(() => {
    logComparisonValues();
  }, []);

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount / 100);
  };

  const countErrors = () => {
    if (!comparisonResult.matching_subscriptions || comparisonResult.matching_subscriptions.length === 0) {
      return 0;
    }

    let errorCount = 0;
    const subscription = comparisonResult.matching_subscriptions[0].subscription || comparisonResult.matching_subscriptions[0];
    const customer = comparisonResult.matching_subscriptions[0].customer || {};

    console.log('=== ERROR COUNTING DEBUG ===');
    console.log('Subscription:', subscription);
    console.log('Customer:', customer);

    // Check Customer match - exact same logic as display
    const sofCustomer = (comparisonResult.sof_data.billing_contact_information?.customer || '').toLowerCase();
    const chargebeeCustomer = (customer.company || '').toLowerCase();
    const customerMatch = sofCustomer === chargebeeCustomer;
    console.log(`Customer - SOF: "${sofCustomer}", Chargebee: "${chargebeeCustomer}", Match: ${customerMatch}`);
    if (!customerMatch) errorCount++;

    // Check Email match - exact same logic as display
    const sofEmail = (comparisonResult.sof_data.billing_contact_information?.email_address || '').toLowerCase();
    const chargebeeEmail = (customer.email || '').toLowerCase();
    const emailMatch = sofEmail === chargebeeEmail;
    console.log(`Email - SOF: "${sofEmail}", Chargebee: "${chargebeeEmail}", Match: ${emailMatch}`);
    if (!emailMatch) errorCount++;

    // Check Total Amount match - use proper MRR calculation matching ComplianceReport.js
    const sofAmount = parseFloat((comparisonResult.sof_data.product_information?.total_net_price || '0').replace(/[^0-9.-]+/g, ''));
    
    // Calculate Chargebee amount using the same logic as frontend display
    let chargebeeAmount;
    if (subscription.mrr !== undefined && subscription.mrr > 0) {
      chargebeeAmount = subscription.mrr / 100; // Convert cents to dollars
    } else if (subscription.calculated_total_amount_dollars !== undefined) {
      chargebeeAmount = subscription.calculated_total_amount_dollars;
    } else if (subscription.subscription_items && Array.isArray(subscription.subscription_items)) {
      const totalCents = subscription.subscription_items.reduce((total, item) => total + (item.amount || 0), 0);
      chargebeeAmount = totalCents / 100;
    } else {
      const amount = subscription.plan_amount || subscription.amount || 0;
      chargebeeAmount = amount / 100;
    }
    
    // Allow for small rounding differences (within $0.01)
    const amountDifference = Math.abs(sofAmount - chargebeeAmount);
    const amountMatch = amountDifference < 0.01;
    console.log(`Amount - SOF: ${sofAmount}, Chargebee: ${chargebeeAmount}, Difference: ${amountDifference}, Match: ${amountMatch}`);
    if (!amountMatch) errorCount++;

    // Check Product Instance Domain match - exact same logic as display
    const sofProductDomain = comparisonResult.sof_data.product_information?.product_instance_domain || '';
    
    // Debug all possible locations for chargebee domain
    console.log('=== DOMAIN DEBUGGING ===');
    console.log('subscription.cf_values:', subscription.cf_values);
    console.log('subscription.cf_product_instance_domain:', subscription.cf_product_instance_domain);
    console.log('subscription.custom_fields:', subscription.custom_fields);
    console.log('All subscription keys:', Object.keys(subscription));
    
    const chargebeeProductDomain = subscription.cf_values?.product_instance_domain || 
                                 subscription.cf_product_instance_domain || 
                                 subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value || 
                                 '';
    
    // Also check if it's stored in a different key format
    let alternativeChargebeeDomain = '';
    for (const key in subscription) {
      if (key.includes('product_instance_domain') || key.includes('domain')) {
        console.log(`Found domain-related key: ${key} = ${subscription[key]}`);
        if (subscription[key] && !alternativeChargebeeDomain) {
          alternativeChargebeeDomain = subscription[key];
        }
      }
    }
    
    const finalChargebeeDomain = chargebeeProductDomain || alternativeChargebeeDomain || subscription.id || '';
    
    // Consider it a match if:
    // 1. SOF domain contains the Chargebee domain (same logic as display)
    // 2. SOF domain equals the Chargebee domain
    // 3. Both are empty (no error if both missing)
    const domainMatch = (!finalChargebeeDomain && !sofProductDomain) || // Both empty
                       (finalChargebeeDomain && sofProductDomain && 
                        (sofProductDomain === finalChargebeeDomain || sofProductDomain.includes(finalChargebeeDomain)));
    console.log(`Domain - SOF: "${sofProductDomain}", Chargebee: "${finalChargebeeDomain}", Match: ${domainMatch}`);
    console.log('=== END DOMAIN DEBUGGING ===');
    if (!domainMatch) errorCount++;

    console.log(`Total Error Count: ${errorCount}`);
    console.log('=== END ERROR COUNTING DEBUG ===');

    return errorCount;
  };

  const handleSaveComparison = async () => {
    setSaving(true);
    try {
      let errors, status;
      
      if (comparisonResult.match_count === 0) {
        // No matches found - mark as Subscription Not Found
        errors = -1; // Use -1 to indicate Subscription Not Found
        status = 'Subscription Not Found';
      } else {
        // Matches found - count actual errors
        errors = countErrors();
        status = errors === 0 ? 'Compliant' : 'Needs Review';
      }
      
      // Generate SOF ID from product instance info or use timestamp
      const sofId = comparisonResult.sof_data.product_information?.product_instance_id || 
                   comparisonResult.sof_data.product_information?.product_instance_domain || 
                   `SOF_${Date.now()}`;

      const payload = {
        sof_id: sofId,
        user_hash: user?.user_hash || localStorage.getItem('userHash'),
        upload_date: uploadDate || new Date().toISOString(),
        sof_data: comparisonResult.sof_data,
        subscription_data: (comparisonResult.matching_subscriptions && comparisonResult.matching_subscriptions.length > 0) 
          ? comparisonResult.matching_subscriptions[0] 
          : {},
        comparison_results: {
          match_count: comparisonResult.match_count,
          message: comparisonResult.message,
          matching_subscriptions: comparisonResult.matching_subscriptions || []
        },
        errors: errors,
        status: status,
        signature: false, // ComparisonModal doesn't have signature detection yet
        signature_date: null
      };

      const userHash = user?.user_hash || localStorage.getItem('userHash');
      await axiosInstance.post('/api/save-comparison', payload, {
        headers: {
          Authorization: userHash
        }
      });
      
      alert(`Comparison saved successfully! Status: ${status}, Errors: ${errors}`);
      onClose();
    } catch (error) {
      console.error('Error saving comparison:', error);
      alert(resolve403ErrorMessage(error, 'Failed to save comparison: ' + (error.response?.data?.error || error.message)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-[1600px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              SOF to Chargebee Subscription Comparison
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mt-4 flex gap-4" style={{ height: 'calc(100vh - 250px)' }}>
            {comparisonResult.match_count > 0 && uploadedFile && (
              <div className="w-1/2 border rounded-lg p-4 overflow-hidden bg-gray-50 flex flex-col">
                <h4 className="font-semibold text-gray-900 mb-3">Uploaded SOF Document</h4>
                <div className="pdf-viewer-container flex-1">
                  <embed
                    src={`${URL.createObjectURL(uploadedFile)}#toolbar=0&navpanes=0&scrollbar=1`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ minHeight: '600px' }}
                  />
                </div>
              </div>
            )}
            
            <div className={`${comparisonResult.match_count > 0 ? 'w-1/2' : 'w-full'} overflow-auto`}>
              {comparisonResult.match_count === 0 ? (
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-red-800 font-medium">{comparisonResult.message}</p>
                  {comparisonResult.suggestions && (
                    <div className="mt-3">
                      <p className="text-sm text-red-700 font-medium">Suggestions:</p>
                      <ul className="list-disc list-inside text-sm text-red-600 mt-1">
                        {comparisonResult.suggestions.map((suggestion, idx) => (
                          <li key={idx}>{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Display SOF Data when no matches found */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Extracted SOF Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">Contact Information</h5>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Customer:</span> {comparisonResult.sof_data.billing_contact_information?.customer || 'N/A'}</p>
                        <p><span className="font-medium">Contact Name:</span> {comparisonResult.sof_data.billing_contact_information?.contact_name || 'N/A'}</p>
                        <p><span className="font-medium">Email:</span> {comparisonResult.sof_data.billing_contact_information?.email_address || 'N/A'}</p>
                        <p><span className="font-medium">Phone:</span> {comparisonResult.sof_data.billing_contact_information?.phone || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">Product Information</h5>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Product Instance ID:</span> {comparisonResult.sof_data.product_information?.product_instance_id || 'N/A'}</p>
                        <p><span className="font-medium">Product Instance Domain:</span> {comparisonResult.sof_data.product_information?.product_instance_domain || 'N/A'}</p>
                        <p><span className="font-medium">Item Name:</span> {comparisonResult.sof_data.product_information?.item_name || 'N/A'}</p>
                        <p><span className="font-medium">Total Net Price:</span> {comparisonResult.sof_data.product_information?.total_net_price || 'N/A'}</p>
                        <p><span className="font-medium">Quantity:</span> {comparisonResult.sof_data.product_information?.quantity || 'N/A'}</p>
                        <p><span className="font-medium">Duration:</span> {comparisonResult.sof_data.product_information?.duration_months || 'N/A'} months</p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">Subscription Details</h5>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Start Date:</span> {comparisonResult.sof_data.subscription_details?.subscription_term_start_date || 'N/A'}</p>
                        <p><span className="font-medium">End Date:</span> {comparisonResult.sof_data.subscription_details?.subscription_term_end_date || 'N/A'}</p>
                        <p><span className="font-medium">Billing Frequency:</span> {comparisonResult.sof_data.subscription_details?.billing_frequency || 'N/A'}</p>
                        <p><span className="font-medium">Payment Method:</span> {comparisonResult.sof_data.subscription_details?.payment_method || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">Billing Address</h5>
                      <div className="space-y-1 text-sm">
                        <p className="whitespace-pre-wrap">{comparisonResult.sof_data.billing_contact_information?.bill_to_address || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 font-medium">{comparisonResult.message}</p>
                  
                </div>

                {comparisonResult.matching_subscriptions.map((match, idx) => {
                  // The backend returns the entire Chargebee response structure
                  const subscription = match.subscription || match;
                  const customer = match.customer || {};
                  const plan = match.plan || {};
                  
                  // Debug logging to see the subscription structure
                  console.log('Full match data:', match);
                  console.log('Subscription data:', subscription);
                  console.log('Custom fields from subscription:', subscription.cf_values);
                  
                  return (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        Subscription #{idx + 1} {subscription.id && `(${subscription.id})`}
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-gray-700 mb-2">Customer Details</h5>
                          <div className="space-y-1 text-sm">
                            <p><span className="font-medium">Company:</span> {customer.company || 'N/A'}</p>
                            <p><span className="font-medium">Email:</span> {customer.email || 'N/A'}</p>
                            <p><span className="font-medium">Name:</span> {customer.first_name} {customer.last_name}</p>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-gray-700 mb-2">Subscription Details</h5>
                          <div className="space-y-1 text-sm">
                            <p><span className="font-medium">Status:</span> 
                              <span className={`ml-1 px-2 py-1 rounded text-xs ${
                                subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                                subscription.status === 'in_trial' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {subscription.status}
                              </span>
                            </p>
                            <p><span className="font-medium">Plan:</span> {plan.name || subscription.plan_id}</p>
                            <p><span className="font-medium">Amount:</span> 
                              {formatCurrency(subscription.mrr || 0, subscription.currency_code)}
                              /{subscription.billing_period_unit || 'month'}
                            </p>
                            <p><span className="font-medium">Quantity:</span> {subscription.plan_quantity || 1}</p>
                            <p><span className="font-medium">Term End:</span> 
                              {subscription.current_term_end ? 
                                new Date(subscription.current_term_end * 1000).toLocaleDateString() : 
                                'N/A'
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t">
                        <h5 className="font-medium text-gray-700 mb-3">Comparison with SOF</h5>
                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                          <div className="grid grid-cols-3 text-sm">
                            <div className="bg-gray-100 border-b border-r border-gray-300 p-3 font-semibold text-gray-700">
                              Field
                            </div>
                            <div className="bg-gray-100 border-b border-r border-gray-300 p-3 font-semibold text-gray-700">
                              SOF Value
                            </div>
                            <div className="bg-gray-100 border-b border-gray-300 p-3 font-semibold text-gray-700">
                              Chargebee Value
                            </div>
                            
                            <div className="border-b border-r border-gray-300 p-3 bg-white font-medium">Customer</div>
                            <div className={`border-b border-r border-gray-300 p-3 bg-white ${
                              (comparisonResult.sof_data.billing_contact_information?.customer || '').toLowerCase() === 
                              (customer.company || '').toLowerCase() ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {comparisonResult.sof_data.billing_contact_information?.customer || 'N/A'}
                            </div>
                            <div className="border-b border-gray-300 p-3 bg-white font-bold">{customer.company || 'N/A'}</div>
                            
                            <div className="border-b border-r border-gray-300 p-3 bg-gray-50 font-medium">Email</div>
                            <div className={`border-b border-r border-gray-300 p-3 bg-gray-50 ${
                              (comparisonResult.sof_data.billing_contact_information?.email_address || '').toLowerCase() === 
                              (customer.email || '').toLowerCase() ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {comparisonResult.sof_data.billing_contact_information?.email_address || 'N/A'}
                            </div>
                            <div className="border-b border-gray-300 p-3 bg-gray-50 font-bold">{customer.email || 'N/A'}</div>
                            
                            <div className="border-b border-r border-gray-300 p-3 bg-white font-medium">Total Amount</div>
                            <div className={`border-b border-r border-gray-300 p-3 bg-white ${
                              (() => {
                                const sofAmount = parseFloat((comparisonResult.sof_data.product_information?.total_net_price || '0').replace(/[^0-9.-]+/g, ''));
                                
                                // Calculate Chargebee amount using the same logic as error counting
                                let chargebeeAmount;
                                if (subscription.mrr !== undefined && subscription.mrr > 0) {
                                  chargebeeAmount = subscription.mrr / 100;
                                } else if (subscription.calculated_total_amount_dollars !== undefined) {
                                  chargebeeAmount = subscription.calculated_total_amount_dollars;
                                } else if (subscription.subscription_items && Array.isArray(subscription.subscription_items)) {
                                  const totalCents = subscription.subscription_items.reduce((total, item) => total + (item.amount || 0), 0);
                                  chargebeeAmount = totalCents / 100;
                                } else {
                                  const amount = subscription.plan_amount || subscription.amount || 0;
                                  chargebeeAmount = amount / 100;
                                }
                                
                                // Allow for small rounding differences (within $0.01)
                                const amountDifference = Math.abs(sofAmount - chargebeeAmount);
                                const amountMatch = amountDifference < 0.01;
                                
                                return amountMatch ? 'text-green-600' : 'text-red-600';
                              })()
                            }`}>
                              {comparisonResult.sof_data.product_information?.total_net_price || 'N/A'}
                            </div>
                            <div className="border-b border-gray-300 p-3 bg-white font-bold">
                              {formatCurrency(subscription.mrr || 0, subscription.currency_code)}
                            </div>
                            
                            <div className="border-r border-gray-300 p-3 bg-gray-50 font-medium">Product Instance Domain</div>
                            <div className={`border-r border-gray-300 p-3 bg-gray-50 ${
                              (() => {
                                const sofProductDomain = comparisonResult.sof_data.product_information?.product_instance_domain || '';
                                // Get the actual Chargebee product instance domain using same logic as error counting
                                const chargebeeProductDomain = subscription.cf_values?.product_instance_domain || 
                                                             subscription.cf_product_instance_domain || 
                                                             subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value || 
                                                             '';
                                                             
                                // Also check alternative locations
                                let alternativeChargebeeDomain = '';
                                for (const key in subscription) {
                                  if (key.includes('product_instance_domain') || key.includes('domain')) {
                                    if (subscription[key] && !alternativeChargebeeDomain) {
                                      alternativeChargebeeDomain = subscription[key];
                                    }
                                  }
                                }
                                
                                const finalChargebeeDomain = chargebeeProductDomain || alternativeChargebeeDomain || subscription.id || '';
                                
                                // Consider it a match if SOF domain contains or equals the Chargebee domain, or both are empty
                                const match = (!finalChargebeeDomain && !sofProductDomain) || 
                                            (finalChargebeeDomain && sofProductDomain && 
                                             (sofProductDomain === finalChargebeeDomain || sofProductDomain.includes(finalChargebeeDomain)));
                                return match ? 'text-green-600' : 'text-red-600';
                              })()
                            }`}>
                              {comparisonResult.sof_data.product_information?.product_instance_domain || 'N/A'}
                            </div>
                            <div className="p-3 bg-gray-50 font-bold">
                              {(() => {
                                // Use same logic as error counting for consistency
                                const productDomain = subscription.cf_values?.product_instance_domain || 
                                                    subscription.cf_product_instance_domain || 
                                                    subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value;
                                
                                if (productDomain) return productDomain;
                                
                                // Check alternative locations
                                for (const key in subscription) {
                                  if (key.includes('product_instance_domain') || key.includes('domain')) {
                                    if (subscription[key]) return subscription[key];
                                  }
                                }
                                
                                return 'N/A';
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleSaveComparison}
              disabled={saving}
              className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Comparison'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComparisonModal;